import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useNavigate, useParams } from "react-router-dom";

import {
  getAdmissionById,
  updateAdmission,
  deleteAdmission,
} from "../api/admissionssApi";
import { listHospitals } from "../api/hospitalsApi";
import { prettyError } from "../utils/prettyError";
import { Colors } from "../../utils/Colors";
import { ROUTES } from "../../shared/constants/routes";

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "DISCHARGED", label: "Discharged" },
  { value: "CANCELLED", label: "Cancelled" },
];

const ADMIT_SOURCE_OPTIONS = [
  { value: "ER", label: "ER" },
  { value: "OPD", label: "OPD" },
  { value: "TRANSFER", label: "Transfer" },
  { value: "OTHER", label: "Other" },
];

function pickFirst(...values) {
  return values.find((v) => v !== undefined && v !== null && v !== "");
}

function fmt(v) {
  if (v === undefined || v === null || v === "") return "—";
  return String(v);
}

function getHospitalId(h) {
  if (!h) return null;
  if (typeof h !== "object") return h;
  return h.id ?? h.pk ?? h.uuid ?? h.hospital_id ?? h.organization_id ?? null;
}

function getHospitalName(h) {
  if (!h) return null;
  if (typeof h !== "object") return String(h);
  return h.name ?? h.hospital_name ?? h.organization_name ?? h.title ?? null;
}

function makeHospitalMap(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    const id = getHospitalId(row);
    if (id !== null && id !== undefined) {
      map[String(id)] = row;
    }
  });
  return map;
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function toLocalInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const mins = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${mins}`;
}

function toIsoOrNull(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "190px 1fr" },
        gap: 1,
        py: 1.2,
        borderBottom: `1px solid ${Colors.border.light}`,
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 13, color: Colors.text.header }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, color: Colors.text.body }}>
        {fmt(value)}
      </Typography>
    </Box>
  );
}

export default function AdmissionDetailPage() {
  const navigate = useNavigate();
  const { admissionId } = useParams();

  const [data, setData] = React.useState(null);
  const [hospitalName, setHospitalName] = React.useState("");
  const [hospitals, setHospitals] = React.useState([]);

  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const [editForm, setEditForm] = React.useState({
    organization: "",
    department: "",
    ward_unit: "",
    bed_number: "",
    admit_at: "",
    discharge_at: "",
    admit_source: "",
    status: "",
    treating_doctor: "",
    consultant: "",
  });

  async function loadDetail() {
    setLoading(true);
    setError("");

    try {
      const [detail, hospitalsData] = await Promise.all([
        getAdmissionById(admissionId),
        listHospitals(),
      ]);

      const hospitalsMap = makeHospitalMap(hospitalsData || []);

      const hospitalRef =
        detail?.organization ??
        detail?.organization_id ??
        detail?.hospital ??
        detail?.hospital_id ??
        null;

      const hospitalObj =
        typeof hospitalRef === "object" && hospitalRef !== null
          ? hospitalRef
          : hospitalsMap[String(hospitalRef)] || null;

      setHospitalName(
        pickFirst(
          detail?.organization_name,
          detail?.hospital_name,
          getHospitalName(hospitalObj)
        )
      );

      setHospitals(hospitalsData || []);
      setData(detail);
    } catch (e) {
      setError(prettyError(e));
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (admissionId) {
      loadDetail();
    }
  }, [admissionId]);

  function openEditDialog() {
    if (!data) return;

    setEditForm({
      organization: pickFirst(
        data?.organization,
        data?.organization_id,
        data?.hospital,
        data?.hospital_id,
        ""
      ),
      department: data?.department || "",
      ward_unit: data?.ward_unit || "",
      bed_number: data?.bed_number || "",
      admit_at: toLocalInputValue(data?.admit_at),
      discharge_at: toLocalInputValue(data?.discharge_at),
      admit_source: data?.admit_source || "",
      status: data?.status || "ACTIVE",
      treating_doctor: pickFirst(data?.treating_doctor, data?.attending_doctor, ""),
      consultant: data?.consultant || "",
    });

    setEditOpen(true);
  }

  function handleEditChange(key) {
    return (e) => {
      setEditForm((prev) => ({ ...prev, [key]: e.target.value }));
    };
  }

  async function handleUpdate() {
    if (!admissionId) return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        organization: editForm.organization || null,
        department: editForm.department || null,
        ward_unit: editForm.ward_unit || null,
        bed_number: editForm.bed_number || null,
        admit_at: toIsoOrNull(editForm.admit_at),
        discharge_at: toIsoOrNull(editForm.discharge_at),
        admit_source: editForm.admit_source || null,
        status: editForm.status || null,
        treating_doctor: editForm.treating_doctor || null,
        consultant: editForm.consultant || null,
      };

      await updateAdmission(admissionId, payload);
      setSuccess("Admission updated successfully.");
      setEditOpen(false);
      await loadDetail();
    } catch (e) {
      setError(prettyError(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!admissionId) return;

    setDeleting(true);
    setError("");
    setSuccess("");

    try {
      await deleteAdmission(admissionId);
      setDeleteOpen(false);
      navigate(ROUTES.ADMISSIONS_LIST);
    } catch (e) {
      setError(prettyError(e));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Box sx={{ p: 2, background: Colors.bg.app, minHeight: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Card
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 900,
            borderRadius: 3,
            borderColor: Colors.border.light,
            background: Colors.bg.paper,
            boxShadow: Colors.shadow.brand,
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ mb: 1 }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(ROUTES.ADMISSIONS_LIST)}
                sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}
              >
                Back
              </Button>

              {data ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<EditOutlinedIcon />}
                    onClick={openEditDialog}
                    sx={{
                      backgroundColor: Colors.brand.primary,
                      "&:hover": { backgroundColor: Colors.brand.primaryDark },
                    }}
                  >
                    Update
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineOutlinedIcon />}
                    onClick={() => setDeleteOpen(true)}
                  >
                    Delete
                  </Button>
                </Stack>
              ) : null}
            </Stack>

            <Typography variant="h6" sx={{ fontWeight: 900, color: Colors.text.title }}>
              Admission Details
            </Typography>

            <Divider sx={{ my: 2, borderColor: Colors.border.topLine }} />

            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}

            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            ) : null}

            {loading ? (
              <Box
                sx={{
                  p: 3,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={22} />
                <Typography>Loading details...</Typography>
              </Box>
            ) : data ? (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  borderColor: Colors.border.light,
                  p: 2,
                }}
              >
                <DetailRow label="Admission ID" value={pickFirst(data?.admission_id, data?.id)} />
                <DetailRow label="Admission No" value={data?.admission_no} />
                <DetailRow label="Patient Name" value={data?.patient_name} />
                <DetailRow
                  label="UHID"
                  value={pickFirst(
                    data?.patient_uhid_out,
                    data?.patient_uhid,
                    data?.uhid
                  )}
                />
                <DetailRow label="Hospital Name" value={hospitalName} />
                <DetailRow label="Department" value={data?.department} />
                <DetailRow label="Ward / Unit" value={data?.ward_unit} />
                <DetailRow label="Bed Number" value={data?.bed_number} />
                <DetailRow label="Admit At" value={formatDateTime(data?.admit_at)} />
                <DetailRow label="Discharge At" value={formatDateTime(data?.discharge_at)} />
                <DetailRow label="Admit Source" value={data?.admit_source} />
                <DetailRow
                  label="Treating Doctor"
                  value={pickFirst(data?.treating_doctor, data?.attending_doctor)}
                />
                <DetailRow label="Consultant" value={data?.consultant} />
                <DetailRow label="Status" value={data?.status} />
                <DetailRow label="Created At" value={formatDateTime(data?.created_at)} />
                <DetailRow label="Updated At" value={formatDateTime(data?.updated_at)} />
              </Paper>
            ) : null}
          </CardContent>
        </Card>
      </Box>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Update Admission</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.2 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Hospital"
                value={editForm.organization}
                onChange={handleEditChange("organization")}
              >
                {(hospitals || []).map((h) => {
                  const id = getHospitalId(h);
                  const name = getHospitalName(h);
                  return (
                    <MenuItem key={String(id)} value={String(id)}>
                      {name}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Department"
                value={editForm.department}
                onChange={handleEditChange("department")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ward / Unit"
                value={editForm.ward_unit}
                onChange={handleEditChange("ward_unit")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bed Number"
                value={editForm.bed_number}
                onChange={handleEditChange("bed_number")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                type="datetime-local"
                fullWidth
                label="Admit At"
                InputLabelProps={{ shrink: true }}
                value={editForm.admit_at}
                onChange={handleEditChange("admit_at")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                type="datetime-local"
                fullWidth
                label="Discharge At"
                InputLabelProps={{ shrink: true }}
                value={editForm.discharge_at}
                onChange={handleEditChange("discharge_at")}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Admit Source"
                value={editForm.admit_source}
                onChange={handleEditChange("admit_source")}
              >
                {ADMIT_SOURCE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={editForm.status}
                onChange={handleEditChange("status")}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Treating Doctor"
                value={editForm.treating_doctor}
                onChange={handleEditChange("treating_doctor")}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Consultant"
                value={editForm.consultant}
                onChange={handleEditChange("consultant")}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Admission</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete this admission?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}